import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { captureAndReportError } from '../errorReporting';
import { decryptS3Config } from '../s3CredentialManager';
import { BaseProvider } from './BaseProvider';
import type {
  ProviderConfig,
  UploadOptions,
  UploadResult,
  WalletAdapter,
} from './types';
export interface S3Config {
  accessKeyId: string;
  secretAccessKey: string;
  bucketName: string;
  region?: string;
  pathPrefix?: string;
  cloudFrontDomain?: string;
}

export class S3Provider extends BaseProvider {
  name: 's3' = 's3' as const;
  private client: S3Client | null = null;
  private config: S3Config | null = null;
  private walletAdapter: WalletAdapter | null = null;

  async initialize(config?: ProviderConfig): Promise<void> {
    if (config?.walletAdapter) {
      this.walletAdapter = config.walletAdapter;
    }

    if (!this.config) {
      const s3Config = await this.loadS3Config();
      if (!s3Config) {
        throw new Error(
          'S3 configuration not found. Please configure your S3 credentials in preferences.',
        );
      }
      this.config = s3Config;
    }

    this.client = new S3Client({
      region: this.config.region || 'us-east-1',
      credentials: {
        accessKeyId: this.config.accessKeyId,
        secretAccessKey: this.config.secretAccessKey,
      },
    });
  }

  async uploadFromBuffer(
    buffer: Buffer,
    options?: UploadOptions,
  ): Promise<UploadResult> {
    if (!this.client || !this.config) {
      throw new Error('S3 provider not initialized');
    }

    const client = this.client;
    const config = this.config;

    return this.retryWithExponentialBackoff(
      async () => {
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const extension = this.getExtensionFromContentType(
          options?.contentType,
          {
            leadingDot: true,
          },
        );
        const fileName = `${timestamp}-${randomId}${extension}`;

        const key = config.pathPrefix
          ? `${config.pathPrefix}/${fileName}`
          : fileName;

        const command = new PutObjectCommand({
          Bucket: config.bucketName,
          Key: key,
          Body: buffer,
          ContentType: options?.contentType || 'application/octet-stream',
          Metadata: options?.tags?.reduce(
            (acc, tag) => {
              acc[tag.name] = tag.value;
              return acc;
            },
            {} as Record<string, string>,
          ),
        });

        if (options?.onProgress) {
          this.reportProgress(0, buffer.length, options.onProgress, {
            message: 'Starting S3 upload...',
          });
        }

        await client.send(command);

        if (options?.onProgress) {
          this.reportProgress(
            buffer.length,
            buffer.length,
            options.onProgress,
            {
              message: 'Upload complete',
            },
          );
        }

        const url = this.generatePublicUrl(key);

        return {
          id: key,
          url: url,
          timestamp: Date.now(),
          size: buffer.length,
        };
      },
      {
        operationName: 'upload_from_buffer',
        maxRetries: options?.maxRetries || 3,
        onRetry: () => {},
      },
    );
  }

  private async loadS3Config(): Promise<S3Config | null> {
    try {
      const config = await decryptS3Config(this.walletAdapter);
      return config;
    } catch (error) {
      captureAndReportError('Failed to load S3 configuration:', error);
      return null;
    }
  }

  async testConfiguration(): Promise<boolean> {
    if (!this.client || !this.config) {
      throw new Error('S3 provider not initialized');
    }

    try {
      const testData = Buffer.from('S3 configuration test');
      const testKey = `${this.config.pathPrefix ? `${this.config.pathPrefix}/` : ''}.test-${Date.now()}.txt`;

      const command = new PutObjectCommand({
        Bucket: this.config.bucketName,
        Key: testKey,
        Body: testData,
        ContentType: 'text/plain',
      });

      await this.client.send(command);
      return true;
    } catch (error) {
      captureAndReportError('S3 configuration test failed:', error);
      throw new Error(
        `S3 configuration test failed: ${this.getErrorMessage(error)}`,
      );
    }
  }

  private generatePublicUrl(key: string): string {
    if (!this.config) {
      throw new Error('S3 provider configuration not loaded');
    }

    const { cloudFrontDomain, bucketName, region } = this.config;

    if (cloudFrontDomain) {
      return `https://${cloudFrontDomain}/${key}`;
    }

    return `https://${bucketName}.s3.${region || 'us-east-1'}.amazonaws.com/${key}`;
  }
}
